import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Bot, User, Sparkles, Cpu, Zap, Loader2 } from "lucide-react";
import { useOmniCraftStore } from "./store";
import { streamOmniCraft, parseStreamedXml, reconcile3Way } from "./stream";
import PlanApprovalCard from "./PlanApprovalCard";
import AgentCommandCenter from "./AgentCommandCenter";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatPane({ token }) {
  const s = useOmniCraftStore();
  const [prompt, setPrompt] = useState("");
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/omnicraft/models`, {
          headers: { "X-Admin-Token": token },
        });
        if (!res.ok) return;
        const data = await res.json();
        s.setModels(data.models || []);
      } catch (e) {
        // silent
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [s.history, s.streamingText, s.plan]);

  const currentModel = s.models.find((m) => m.id === s.modelId);

  async function runPlan(userPrompt) {
    s.setSwarmActive("architect");
    const taskId = crypto.randomUUID();
    s.addSubtask({ id: taskId, agent: "architect", label: "Draft build plan", tool: "plan()", status: "in_progress" });
    s.setStreaming(true, "plan");
    s.resetStreamingText();
    abortRef.current = new AbortController();
    let raw = "";
    try {
      await streamOmniCraft(
        `${API}/api/admin/omnicraft/plan`,
        token,
        { model_id: s.modelId, prompt: userPrompt, files: s.files, history: s.history, ask_before_building: true },
        (event, data) => {
          if (event === "token") {
            raw += data;
            s.appendStreamingText(data);
          } else if (event === "error") {
            const err = JSON.parse(data);
            throw new Error(err.detail || "Plan failed");
          } else if (event === "phase") {
            s.pushLog(`[plan] phase ${data}`);
          }
        },
        abortRef.current.signal,
      );
      const parsed = parseStreamedXml(raw);
      if (!parsed.plan) throw new Error("Model did not return a <plan> block.");
      s.setPlan(parsed.plan);
      s.setPendingPrompt(userPrompt);
      s.updateSubtask(taskId, { status: "passed" });
      s.appendHistory({ role: "assistant", content: parsed.plan.summary || "Plan drafted.", plan: parsed.plan });
    } catch (e) {
      s.updateSubtask(taskId, { status: "failed" });
      s.pushLog(`[error] plan: ${e.message}`);
      toast.error(e.message || "Plan failed");
    } finally {
      s.setStreaming(false);
      s.setSwarmActive("orchestrator");
    }
  }

  async function runBuild(userPrompt) {
    s.setSwarmActive("builder");
    s.snapshotBase();
    const taskId = crypto.randomUUID();
    s.addSubtask({ id: taskId, agent: "builder", label: "Generate files", tool: "write_file()", status: "in_progress" });
    s.setStreaming(true, "build");
    s.resetStreamingText();
    abortRef.current = new AbortController();
    let raw = "";
    let lastAppliedLen = 0;
    try {
      await streamOmniCraft(
        `${API}/api/admin/omnicraft/build`,
        token,
        { model_id: s.modelId, prompt: userPrompt, files: s.files, history: s.history },
        (event, data) => {
          if (event === "token") {
            raw += data;
            s.appendStreamingText(data);
            if (raw.length - lastAppliedLen > 400) {
              lastAppliedLen = raw.length;
              const partial = parseStreamedXml(raw);
              if (Object.keys(partial.files).length > 0) {
                applyMerged(partial.files);
              }
            }
          } else if (event === "error") {
            const err = JSON.parse(data);
            throw new Error(err.detail || "Build failed");
          } else if (event === "phase") {
            s.pushLog(`[build] ${data}`);
          }
        },
        abortRef.current.signal,
      );
      const parsed = parseStreamedXml(raw);
      applyMerged(parsed.files);
      s.updateSubtask(taskId, { status: "passed" });
      s.appendHistory({
        role: "assistant",
        content: parsed.explanation || `Built ${Object.keys(parsed.files).length} file(s).`,
      });
      s.pushLog(`[ok] Applied ${Object.keys(parsed.files).length} file(s)`);
      s.triggerPreview();
      s.setPlan(null);
      s.setPendingPrompt(null);
      toast.success("Build applied");
    } catch (e) {
      s.updateSubtask(taskId, { status: "failed" });
      s.pushLog(`[error] build: ${e.message}`);
      toast.error(e.message || "Build failed");
    } finally {
      s.setStreaming(false);
      s.setSwarmActive("orchestrator");
    }
  }

  function applyMerged(aiFiles) {
    const merged = {};
    const conflicts = [];
    for (const [path, ai] of Object.entries(aiFiles)) {
      const base = s.baseSnapshot[path] || "";
      const user = s.files[path] || base;
      const { ok, text } = reconcile3Way(base, user, ai);
      merged[path] = text;
      if (!ok) conflicts.push({ path, base, user, ai });
    }
    s.applyFileMap(merged);
    if (conflicts.length) {
      conflicts.forEach((c) => s.addConflict(c));
      toast.warning(`${conflicts.length} conflict(s) — resolve in editor`);
    }
  }

  async function send() {
    const text = prompt.trim();
    if (!text || s.streaming) return;
    setPrompt("");
    s.appendHistory({ role: "user", content: text });
    s.clearSubtasks();
    if (s.askBeforeBuilding) {
      await runPlan(text);
    } else {
      await runBuild(text);
    }
  }

  async function approvePlan() {
    if (!s.pendingPrompt || !s.plan) return;
    const prompt = `Build the approved plan.\n\nPLAN JSON:\n${JSON.stringify(s.plan, null, 2)}\n\nORIGINAL PROMPT:\n${s.pendingPrompt}`;
    await runBuild(prompt);
  }

  function rejectPlan() {
    s.setPlan(null);
    s.setPendingPrompt(null);
    s.appendHistory({ role: "user", content: "Refine plan — see next prompt." });
  }

  return (
    <div className="h-full flex flex-col bg-[#0B0D12] text-white">
      <div className="border-b border-white/10 p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <select
              data-testid="omnicraft-model-select"
              value={s.modelId}
              onChange={(e) => s.setModelId(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-400"
            >
              <optgroup label="Cloud">
                {s.models.filter((m) => m.runtime === "cloud").map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Local">
                {s.models.filter((m) => m.runtime === "local").map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </optgroup>
            </select>
            {currentModel && (
              <span
                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${
                  currentModel.status === "online"
                    ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.06]"
                    : "text-rose-300 border-rose-500/40 bg-rose-500/[0.06]"
                }`}
                title={currentModel.note}
              >
                {currentModel.runtime === "local" ? <Cpu className="inline mr-1" size={10} /> : <Zap className="inline mr-1" size={10} />}
                {currentModel.status}
              </span>
            )}
          </div>
          <label className="flex items-center gap-2 text-[11px] text-white/70 select-none cursor-pointer">
            <span>Ask Before Building</span>
            <input
              data-testid="omnicraft-ask-toggle"
              type="checkbox"
              checked={s.askBeforeBuilding}
              onChange={(e) => s.setAskBeforeBuilding(e.target.checked)}
              className="sr-only peer"
            />
            <span className="w-8 h-4 rounded-full bg-white/10 peer-checked:bg-emerald-500 relative transition-colors">
              <span className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
            </span>
          </label>
        </div>
        <AgentCommandCenter />
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {s.history.length === 0 && !s.streaming && (
          <div className="text-center text-white/40 text-sm py-8 space-y-2">
            <Sparkles className="mx-auto text-emerald-400" size={20} />
            <div>Ask OmniCraft to build something.</div>
            <div className="text-[11px] text-white/30">
              Try: "Build a Pomodoro timer with round tracking and a session log."
            </div>
          </div>
        )}
        {s.history.map((m, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div
              className={`w-6 h-6 grid place-items-center rounded-md flex-shrink-0 ${
                m.role === "user" ? "bg-white/10" : "bg-emerald-500/20 text-emerald-300"
              }`}
            >
              {m.role === "user" ? <User size={12} /> : <Bot size={12} />}
            </div>
            <div className="flex-1 space-y-2">
              <div className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
                {m.content}
              </div>
            </div>
          </div>
        ))}

        {s.plan && !s.streaming && (
          <PlanApprovalCard
            plan={s.plan}
            onApprove={approvePlan}
            onReject={rejectPlan}
            disabled={s.streaming}
          />
        )}

        {s.streaming && (
          <div className="flex gap-2 items-start">
            <div className="w-6 h-6 grid place-items-center rounded-md bg-emerald-500/20 text-emerald-300">
              <Loader2 size={12} className="animate-spin" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase text-white/40 mb-1">
                Streaming · {s.streamPhase}
              </div>
              <pre className="text-xs text-white/60 whitespace-pre-wrap break-words font-mono max-h-64 overflow-y-auto">
                {s.streamingText.slice(-1200) || "…"}
              </pre>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-white/10 p-3">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="omnicraft-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send();
            }}
            placeholder="Describe what you want to build…  (⌘/Ctrl + Enter to send)"
            rows={2}
            disabled={s.streaming}
            className="flex-1 resize-none bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-400 disabled:opacity-60"
          />
          <button
            data-testid="omnicraft-send"
            onClick={send}
            disabled={s.streaming || !prompt.trim()}
            className="px-3 py-2 rounded-md bg-emerald-500 text-black text-xs font-semibold flex items-center gap-1.5 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={12} />
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
